import { DateFormat, formatDate } from '@leland-dev/leland-ui-library';
import groq from 'groq';
import Link from 'next/link';

import {
  ARTICLE_WRITTEN_AT_PROJECTION_FIELD,
  type ArticleWithWrittenAt,
} from '../../lib/sanity/commonGroqs';
import { getLibraryArticleUrl } from '../../utils/routing';
import SanityImage, { SANITY_IMAGE_PROJECTION } from '../image/SanityImage';

export type ArticleCardArticle = Pick<
  ArticleWithWrittenAt,
  '_id' | 'slug' | 'featuredImage' | 'title' | 'subtitle' | 'writtenAt'
>;

interface ArticleProps {
  article: ArticleCardArticle;
  /** priority loading of article image */
  priority?: boolean;
  compact?: boolean;
  className?: string;
}

export const ARTICLE_CARD_ARTICLE_PROJECTION = groq`
  _id,
  slug,
  featuredImage {${SANITY_IMAGE_PROJECTION}},
  title,
  subtitle,
  ${ARTICLE_WRITTEN_AT_PROJECTION_FIELD}
`;

const ArticleCard: React.FC<ArticleProps> = ({
  article: { slug, featuredImage, title = '', subtitle = '', writtenAt },
  priority,
  compact,
  className = '',
}) => {
  if (!slug) {
    return null;
  }

  return (
    <Link
      href={getLibraryArticleUrl(slug.current)}
      className={`${className} ${compact ? 'flex items-center' : ''}`}
      id="article_card--view_article"
    >
      {featuredImage ? (
        <div
          className={`relative ${
            compact
              ? 'h-24 w-40'
              : 'mb-4 w-full overflow-hidden object-cover pb-65%'
          }`}
        >
          <SanityImage
            className="rounded-lg object-cover"
            image={featuredImage}
            alt=""
            priority={priority}
            sizes={
              compact
                ? { dflt: '10rem' }
                : { lg: '500px', md: '300px', dflt: '90vw' }
            }
            fill
          />
        </div>
      ) : null}
      <div className={compact ? 'w-5/6 pl-6' : undefined}>
        <p className="mb-1 text-sm text-leland-gray-light">
          {formatDate(new Date(writtenAt), DateFormat.LONG_STANDARD)}
        </p>
        <h3
          className={`text-xl font-medium text-leland-gray-dark ${
            compact ? 'line-clamp-1' : 'line-clamp-2'
          }`}
        >
          {title}
        </h3>
        <p
          className={`mt-1 text-base leading-snug text-leland-gray-light ${
            compact ? 'line-clamp-1' : 'line-clamp-3'
          }`}
        >
          {subtitle}
        </p>
      </div>
    </Link>
  );
};

export default ArticleCard;
